import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.jsx';

/**
 * Shared layout for every /account/* route. Renders a left sidebar with
 * grouped sections (ACCOUNT / ORGANIZATION / ADMIN) and slots the route
 * content into the main column on the right.
 *
 * Same pattern CliLancers uses for its Settings area. Each group header
 * is uppercase tracking-wide, items are full-width links that highlight
 * when active. Admin section is owner-only.
 */
export default function AccountLayout({ children }) {
  const { organization } = useAuth();
  const role = organization?.role;
  const isOwner = role === 'owner';
  const isAdmin = isOwner || role === 'admin';

  return (
    <div className="account-shell">
      <aside className="account-sidebar">
        <SidebarGroup title="Account">
          <SidebarItem to="/account"            icon="user"   label="Profile" end />
          <SidebarItem to="/account/password"   icon="key"    label="Password" />
          <SidebarItem to="/account/appearance" icon="paint"  label="Appearance" />
        </SidebarGroup>

        <SidebarGroup title="Organization">
          <SidebarItem to="/account/organization" icon="org"   label="Organization" />
          <SidebarItem to="/account/team"         icon="team"  label="Team" />
          {isAdmin && <SidebarItem to="/account/billing" icon="card" label="Billing" />}
          <SidebarItem to="/account/support" icon="help" label="Support" />
        </SidebarGroup>

        {isAdmin && (
          <SidebarGroup title="Admin">
            <SidebarItem to="/account/roadmap" icon="map"   label="Roadmap" />
            <SidebarItem to="/account/audit"   icon="audit" label="Audit log" />
          </SidebarGroup>
        )}
      </aside>

      <main className="account-main">{children}</main>
    </div>
  );
}

function SidebarGroup({ title, children }) {
  return (
    <div className="sidebar-group">
      <h3 className="sidebar-group-title">{title}</h3>
      <nav className="sidebar-items">{children}</nav>
    </div>
  );
}

const ICON = {
  user:  '👤',
  key:   '🔑',
  paint: '🎨',
  org:   '🏢',
  team:  '👥',
  audit: '📋',
  map:   '🗺️',
  card:  '💳',
  help:  '🛟',
};

function SidebarItem({ to, icon, label, end = false }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}>
      <span className="sidebar-icon" aria-hidden="true">{ICON[icon] || '·'}</span>
      <span className="sidebar-label">{label}</span>
    </NavLink>
  );
}
